/**
 * Post-login "return to" handling. A `returnTo` query param is attacker-controllable, so it is only ever
 * honoured as a same-site relative path; anything else is dropped and the normal flow runs.
 */

const MAX_RETURN_TO_LENGTH = 2048

/** Paths that must never be a return target: they'd loop back into login / onboarding / selection. */
const NON_RETURNABLE_PREFIXES = ['/select-company', '/onboarding', '/auth', '/api']

/** Top-level app routes — a first path segment that is one of these is not a company slug. */
const TOP_LEVEL_ROUTES = new Set(['api', 'auth', 'docs', 'onboarding', 'panel', 'select-company', 'signup', '404'])

const PLACEHOLDER_ORIGIN = 'http://return-to.invalid'

const isUnder = (pathname: string, prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`)

/** Returns the in-app path (`pathname + search + hash`) when `raw` is a safe same-site path, else `null`. */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
    if (!raw || raw.length > MAX_RETURN_TO_LENGTH) return null
    // Single leading slash only: `//host` and `/\host` are protocol-relative to browsers.
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(raw)) return null

    let url: URL
    try {
        url = new URL(raw, PLACEHOLDER_ORIGIN)
    } catch {
        return null
    }
    if (url.origin !== PLACEHOLDER_ORIGIN) return null
    if (NON_RETURNABLE_PREFIXES.some((prefix) => isUnder(url.pathname, prefix))) return null

    return `${url.pathname}${url.search}${url.hash}`
}

/** The company slug a path belongs to (`/acme/recruitment/...` → `acme`), or `null` for top-level routes. */
export function getCompanySlugFromPath(path: string | null | undefined): string | null {
    if (!path) return null
    const segment = path.split(/[?#]/)[0].split('/')[1]
    if (!segment || TOP_LEVEL_ROUTES.has(segment)) return null
    return segment
}
